"use server";

import { redirect } from "next/navigation";
import { createServerClient } from "@supabase/ssr";
import { cookies, headers } from "next/headers";
import { claimSession } from "@/lib/session/claim-session";
import { setSessionTokenCookie, clearSessionTokenCookie } from "@/lib/session/set-session-cookie";

async function supabaseFromCookies() {
  const cookieStore = await cookies();

  return createServerClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      cookies: {
        getAll() {
          return (cookieStore as any).getAll();
        },
        setAll(cookiesToSet) {
          cookiesToSet.forEach(({ name, value, options }) => {
            (cookieStore as any).set(name, value, options);
          });
        },
      },
    }
  );
}

function asNonEmptyString(v: FormDataEntryValue | null, name: string) {
  const s = typeof v === "string" ? v.trim() : "";
  if (!s) throw new Error(`Missing ${name}`);
  return s;
}

async function getSiteUrl() {
  const env = process.env.NEXT_PUBLIC_SITE_URL?.trim();
  if (env) return env.replace(/\/+$/, "");

  const h = await headers();

  // Prefer forwarded headers (Vercel/proxies), fall back to host.
  const proto = h.get("x-forwarded-proto") ?? "http";
  const host = h.get("x-forwarded-host") ?? h.get("host");
  if (host) return `${proto}://${host}`.replace(/\/+$/, "");

  // Final fallback
  return "http://localhost:3000";
}

export async function signIn(formData: FormData) {
  const email = asNonEmptyString(formData.get("email"), "email");
  const password = asNonEmptyString(formData.get("password"), "password");

  const supabase = await supabaseFromCookies();
  const { error } = await supabase.auth.signInWithPassword({ email, password });

  if (error) redirect(`/login?error=${encodeURIComponent(error.message)}`);

  // Claim single-session token (invalidates any previous session)
  const { data: { user } } = await supabase.auth.getUser();
  if (user) {
    const token = await claimSession(user.id);
    await setSessionTokenCookie(token);
  }

  const returnToRaw = formData.get("returnTo");
  const returnTo =
    typeof returnToRaw === "string" && returnToRaw.startsWith("/")
      ? returnToRaw
      : "/app";

  redirect(returnTo);
}

// Used by /forgot-password
export async function requestPasswordReset(formData: FormData) {
  const email = asNonEmptyString(formData.get("email"), "email");
  const supabase = await supabaseFromCookies();

  const siteUrl = await getSiteUrl();

  // Send the user to our domain-confirm route (white-labeled),
  // then land them on reset-password.
  const redirectTo = `${siteUrl}/auth/confirm?type=recovery&next=${encodeURIComponent(
    "/reset-password"
  )}`;

  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo,
  });

  // Always show generic success (avoid account enumeration)
  if (error) redirect(`/forgot-password?sent=1`);
  redirect(`/forgot-password?sent=1`);
}

export async function signOut() {
  const supabase = await supabaseFromCookies();
  await supabase.auth.signOut();
  await clearSessionTokenCookie();
  redirect("/login");
}
